/*
 * Cart drawer complementary-product upsell.
 *
 * Renders a horizontally scrolling row of "Recommended Products" inside the cart
 * drawer, based on Shopify's complementary product recommendations for everything
 * currently in the cart (falls back to "related" recommendations if the store has no
 * complementary products configured). Adding a recommended product refreshes the drawer
 * (reusing cart-drawer.js renderContents) and the upsell row.
 *
 * The markup shell lives in snippets/cart-drawer.liquid ([data-cart-upsell]).
 *
 * All handlers are delegated at the document level so the feature keeps working even
 * when the drawer markup (including this element) is re-rendered on cart changes.
 */
(function () {
  const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // Cap how many distinct cart products we request recommendations for, to avoid firing
  // a large number of requests for big carts.
  const MAX_SOURCE_PRODUCTS = 5;

  let busy = false;
  let trackObserver = null;

  function getRoot() {
    return document.querySelector('[data-cart-upsell]');
  }

  function getLimit(root) {
    return parseInt(root.dataset.limit, 10) || 10;
  }

  async function fetchJSON(url) {
    try {
      return await fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    } catch (e) {
      console.error('[cart-upsell]', e);
      return null;
    }
  }

  async function fetchRecommendations(productId, intent, limit) {
    const url =
      `${ROOT()}recommendations/products.json` +
      `?product_id=${encodeURIComponent(productId)}&intent=${intent}&limit=${limit}`;
    const data = await fetchJSON(url);
    return (data && data.products) || [];
  }

  async function gatherRecommendations(cart, limit) {
    const cartProductIds = [...new Set(cart.items.map((item) => item.product_id))];
    const cartVariantIds = new Set(cart.items.map((item) => item.variant_id));
    const sourceIds = cartProductIds.slice(0, MAX_SOURCE_PRODUCTS);

    const collect = async (intent) => {
      const results = await Promise.all(sourceIds.map((id) => fetchRecommendations(id, intent, limit)));
      const seen = new Set();
      const merged = [];
      results.flat().forEach((product) => {
        if (!product || seen.has(product.id)) return;
        if (cartProductIds.includes(product.id)) return; // don't recommend what's already in the cart
        if ((product.variants || []).some((variant) => cartVariantIds.has(variant.id))) return;
        seen.add(product.id);
        merged.push(product);
      });
      return merged;
    };

    // Only merchant-curated complementary products are shown. If none are configured
    // for the cart's products, the row simply stays hidden (no algorithmic fallback).
    const products = await collect('complementary');
    return products.slice(0, limit);
  }

  async function refresh() {
    const root = getRoot();
    if (!root) return;

    const cart = await fetchJSON(`${ROOT()}cart.js`);
    if (!cart || !cart.items || cart.items.length === 0) {
      hide(root);
      return;
    }

    const products = await gatherRecommendations(cart, getLimit(root));
    if (products.length === 0) {
      hide(root);
      return;
    }

    render(root, products);
    show(root);
  }

  function render(root, products) {
    const track = root.querySelector('[data-cart-upsell-track]');
    if (!track) return;
    track.innerHTML = products.map((product) => cardHTML(product)).join('');
    track.onscroll = () => updateArrows(root);

    // The upsell often renders while the drawer is still hidden (transformed/display
    // none), so the track has no measurable width yet and updateArrows would see no
    // overflow. Recompute whenever the track's box changes (drawer opens, images load,
    // viewport resizes) via a ResizeObserver, and once more after images finish loading.
    if (trackObserver) trackObserver.disconnect();
    trackObserver = new ResizeObserver(() => updateArrows(root));
    trackObserver.observe(track);
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', () => updateArrows(root), { once: true });
    });

    updateArrows(root);
  }

  function cardHTML(product) {
    const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
    if (!variant) return '';

    const url = `${ROOT()}products/${product.handle}`;
    const image = product.featured_image
      ? `<img src="${imageUrl(product.featured_image, 300)}" alt="${escapeHTML(product.title)}" loading="lazy" width="125" height="125">`
      : '';
    const soldOut = !variant.available;

    return `
      <div class="cart-upsell__card">
        <a href="${url}" class="cart-upsell__image" tabindex="-1" aria-hidden="true">${image}</a>
        <a href="${url}" class="cart-upsell__name">${escapeHTML(product.title)}</a>
        ${ratingHTML(product)}
        <span class="cart-upsell__price">${formatMoney(variant.price)}</span>
        <button
          type="button"
          class="cart-upsell__add button"
          data-cart-upsell-add
          data-variant-id="${variant.id}"
          ${soldOut ? 'disabled' : ''}
        >${soldOut ? 'Sold out' : 'Add'}</button>
      </div>`;
  }

  function ratingHTML(product) {
    const map = window.CartUpsellRatings || {};
    const data = map[product.id];
    if (!data || !data.rating) return '';
    const rounded = Math.round(Number(data.rating));
    let stars = '';
    for (let i = 1; i <= 5; i++) stars += i <= rounded ? '★' : '☆';
    const count = Number(data.count) || 0;
    return `
      <div class="cart-upsell__rating" aria-label="Rated ${data.rating} out of 5">
        <span class="cart-upsell__stars" aria-hidden="true">${stars}</span>
        <span class="cart-upsell__reviews">(${count})</span>
      </div>`;
  }

  async function addToCart(button) {
    if (busy || button.hasAttribute('disabled')) return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    busy = true;
    button.setAttribute('disabled', '');
    button.classList.add('loading');

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((s) => s.id) : [];

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const addUrl = window.routes ? routes.cart_add_url : `${ROOT()}cart/add`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData,
      }).then((r) => r.json());

      if (response.status) {
        console.error('[cart-upsell] add failed', response.description || response.message);
        return;
      }

      if (cartDrawer && response.sections && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents({ id: variantId, sections: response.sections });
      }

      document.dispatchEvent(new CustomEvent('discount-ninja:cart-items-changed'));
    } catch (e) {
      console.error('[cart-upsell]', e);
    } finally {
      busy = false;
      button.removeAttribute('disabled');
      button.classList.remove('loading');
    }
  }

  function scrollByCard(direction) {
    const root = getRoot();
    if (!root) return;
    const track = root.querySelector('[data-cart-upsell-track]');
    if (!track) return;
    const card = track.querySelector('.cart-upsell__card');
    const amount = card ? card.getBoundingClientRect().width + 10 : 160;
    track.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  // Only reveal an arrow when the track can actually scroll that direction.
  function updateArrows(root) {
    const track = root.querySelector('[data-cart-upsell-track]');
    const prev = root.querySelector('[data-cart-upsell-prev]');
    const next = root.querySelector('[data-cart-upsell-next]');
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const canScroll = maxScroll > 1;
    if (prev) prev.hidden = !canScroll || track.scrollLeft <= 1;
    if (next) next.hidden = !canScroll || track.scrollLeft >= maxScroll - 1;
  }

  function hide(root) {
    root.hidden = true;
  }

  function show(root) {
    root.hidden = false;
  }

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (e) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function imageUrl(src, width) {
    if (!src) return src;
    const sep = src.indexOf('?') === -1 ? '?' : '&';
    return `${src}${sep}width=${width}`;
  }

  function escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : value;
    return div.innerHTML;
  }

  // --- In-cart "Want to upgrade?" box (snippets/cart-upgrade-offer.liquid) ------------

  function selectUpgradeOption(option) {
    const box = option.closest('[data-cart-upgrade]');
    const optionsEl = option.closest('[data-cart-upgrade-options]');
    if (!box || !optionsEl) return;

    optionsEl.querySelectorAll('.custom-select-option').forEach((o) => o.classList.remove('selected'));
    option.classList.add('selected');

    box.dataset.variantId = option.dataset.variantId;
    const img = box.querySelector('[data-cart-upgrade-img]');
    const title = box.querySelector('[data-cart-upgrade-title]');
    const badge = box.querySelector('[data-cart-upgrade-badge]');
    const priceWrap = box.querySelector('[data-cart-upgrade-price]');
    const compare = box.querySelector('[data-cart-upgrade-compare]');
    const code = box.querySelector('[data-cart-upgrade-code]');
    const triggerSpan = box.querySelector('[data-cart-upgrade-trigger] span');
    if (img) {
      img.src = option.dataset.img;
      img.alt = option.dataset.display;
    }
    if (title) title.textContent = option.dataset.display;
    if (triggerSpan) triggerSpan.textContent = option.dataset.select;
    const disc = option.dataset.discountPrice;
    if (badge) badge.textContent = disc || option.dataset.price;
    if (compare) compare.textContent = option.dataset.price;
    if (priceWrap) priceWrap.classList.toggle('has-discount', !!disc);
    // Each offer carries its own code, since offers can run different promos.
    if (code) code.innerHTML = disc ? option.dataset.discountCode || '' : '';

    optionsEl.classList.remove('open');
    const trigger = box.querySelector('[data-cart-upgrade-trigger]');
    if (trigger) trigger.classList.remove('active');
  }

  function closeUpgradeDropdowns() {
    document.querySelectorAll('[data-cart-upgrade-options].open').forEach((o) => o.classList.remove('open'));
    document.querySelectorAll('[data-cart-upgrade-trigger].active').forEach((t) => t.classList.remove('active'));
  }

  // Upgrade = remove the original line item, then add the selected upsell variant.
  async function upgradeCartItem(box) {
    if (busy || box.classList.contains('is-loading')) return;
    const variantId = box.dataset.variantId;
    const lineKey = box.dataset.lineKey;
    const quantity = parseInt(box.dataset.quantity, 10) || 1;
    if (!variantId || !lineKey) return;

    busy = true;
    box.classList.add('is-loading');

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((s) => s.id) : [];

    try {
      // 1) Add the upgrade variant (matching the original line quantity).
      const addBody = new FormData();
      addBody.append('id', variantId);
      addBody.append('quantity', String(quantity));
      const addUrl = window.routes ? routes.cart_add_url : `${ROOT()}cart/add`;
      const addResp = await fetch(addUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: addBody,
      }).then((r) => r.json());

      if (addResp.status) {
        console.error('[cart-upsell] upgrade add failed', addResp.description || addResp.message);
        return;
      }

      // 2) Remove the original line, requesting the drawer section for a surgical re-render.
      const changeUrl = window.routes ? routes.cart_change_url : `${ROOT()}cart/change`;
      const changeResp = await fetch(changeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());

      if (cartDrawer && changeResp.sections && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents({ id: variantId, sections: changeResp.sections });
      }

      document.dispatchEvent(new CustomEvent('discount-ninja:cart-items-changed'));
    } catch (e) {
      console.error('[cart-upsell] upgrade', e);
    } finally {
      busy = false;
      box.classList.remove('is-loading');
    }
  }

  // Delegated interactions (survive drawer re-renders).
  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-cart-upsell-add]');
    if (addButton) {
      event.preventDefault();
      addToCart(addButton);
      return;
    }
    if (event.target.closest('[data-cart-upsell-prev]')) {
      scrollByCard(-1);
      return;
    }
    if (event.target.closest('[data-cart-upsell-next]')) {
      scrollByCard(1);
      return;
    }

    // Upgrade box: dropdown trigger / option / whole-box click.
    const upgradeTrigger = event.target.closest('[data-cart-upgrade-trigger]');
    if (upgradeTrigger) {
      event.stopPropagation();
      const options = upgradeTrigger.parentElement.querySelector('[data-cart-upgrade-options]');
      if (options) options.classList.toggle('open');
      upgradeTrigger.classList.toggle('active');
      return;
    }
    const upgradeOption = event.target.closest('[data-cart-upgrade-options] .custom-select-option');
    if (upgradeOption) {
      event.stopPropagation();
      selectUpgradeOption(upgradeOption);
      return;
    }
    // Whole box acts as the upgrade trigger (except when interacting with the dropdown).
    const upgradeBox = event.target.closest('[data-cart-upgrade]');
    if (upgradeBox && !event.target.closest('.custom-select')) {
      upgradeCartItem(upgradeBox);
      return;
    }

    // Clicked elsewhere: close any open upgrade dropdown.
    closeUpgradeDropdowns();
  });

  // Refresh on every cart change (cart-drawer.js / cart.js dispatch this after add /
  // quantity / remove).
  document.addEventListener('discount-ninja:cart-items-changed', refresh);

  window.addEventListener('resize', () => {
    const root = getRoot();
    if (root && !root.hidden) updateArrows(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
